import {Component, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {GlobalService} from "@app/_services/global.service";
import {CreateUserRequest, Customer, DeleteUserRequest, GetAllUsersResponse, ProductGroupSummary, SetUserProductGroupsRequest, UpdateUserRequest, User, UserListItem, UserProductGroup} from "@app/_models/api";
import {NgbModal, NgbModalRef, NgbOffcanvas} from "@ng-bootstrap/ng-bootstrap";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {AsyncPipe} from "@angular/common";
import {BehaviorSubject, forkJoin, Observable, Subscription} from "rxjs";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {ToastService} from "@app/_services/toast.service";
import {ApiService} from "@app/_services/api.service";
import {UsersService} from "@app/_services/users/users.service";
import {ActivatedRoute} from "@angular/router";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";
import {openUserActivityDrawer} from "@app/_components/user-activity-drawer/user-activity-drawer.component";

@Component({
    selector: 'app-manage-users',
    imports: [
        TablePagerComponent,
        ReactiveFormsModule,
        FormsModule,
        AsyncPipe,
        ModalConfirmComponent,
        SortableDirective
    ],
    templateUrl: './users.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../app.component.scss']
})
export class UsersComponent implements OnDestroy {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;
    savingData: boolean = false;
    total$: Observable<number>;
    users$: Observable<UserListItem[]>;
    customers: Customer[] = [];
    customerCode: string | undefined;
    addUserForm = new FormGroup({
        customerId: new FormControl("", Validators.required),
        fullName: new FormControl("", Validators.required),
        email: new FormControl("", [Validators.required, Validators.email]),
        isAdmin: new FormControl(false, {nonNullable: true}),
    });
    editForm = new FormGroup({
        id: new FormControl("", Validators.required),
        customerId: new FormControl("", Validators.required),
        fullName: new FormControl("", Validators.required),
        isEnabled: new FormControl(false, Validators.required),
        isAdmin: new FormControl(false, Validators.required),
    });
    @ViewChild('addUserModal') private addUserModal!: TemplateRef<ModalConfirmComponent>;
    @ViewChild('editModal') private editModal!: TemplateRef<ModalConfirmComponent>;
    @ViewChild('productGroupsModal') private productGroupsModal!: TemplateRef<any>;
    productGroupsUser: User | null = null;
    availableProductGroups: ProductGroupSummary[] = [];
    selectedProductGroupIds = new Set<string>();
    loadingProductGroups$ = new BehaviorSubject<boolean>(false);
    addUserProductGroups: ProductGroupSummary[] = [];
    addUserSelectedGroupIds = new Set<string>();
    loadingAddUserProductGroups$ = new BehaviorSubject<boolean>(false);
    private addUserModalRef!: NgbModalRef
    private onEditModalRef!: NgbModalRef
    private productGroupsModalRef!: NgbModalRef
    private _customers$ = new BehaviorSubject<Customer[]>([]);
    private customersSubscription: Subscription;

    constructor(
        public globalService: GlobalService,
        private route: ActivatedRoute,
        private toastService: ToastService,
        private modalService: NgbModal,
        private apiService: ApiService,
        private offcanvasService: NgbOffcanvas,
        public service: UsersService) {

        this._customers$.next(this.route.snapshot.data['resolvedData'].data);
        this.customersSubscription = this._customers$.subscribe(customers => {
            this.customers = customers;
        });

        this.users$ = this.service.data$;
        this.total$ = this.service.total$;
    }

    ngOnDestroy() {
        if (this.customersSubscription) {
            this.customersSubscription.unsubscribe();
        }
    }

    onCustomerChange(event: Event) {
        this.customerCode = (event.target as HTMLInputElement).value;
        const selectedCustomer = this.customers.find(c => c.code === this.customerCode);

        if (selectedCustomer) {
            this.globalService.showWaitOverlay();
            this.apiService.getUsers(selectedCustomer.id, true).subscribe({
                next: (response: GetAllUsersResponse) => {
                    this.service.data = response.users;
                    this.service.setUserCounts(response.customerUserCount, response.maxNumberOfUsers);
                    this.service.refreshDataSource();
                    this.globalService.hideWaitOverlay();
                },
                error: () => {
                    this.toastService.showDanger("Request Failed", "Failed to load users. Please try again.");
                    this.globalService.hideWaitOverlay();
                }
            });
        } else {
            // Clear data if no customer is selected
            this.service.data = [];
            this.service.setUserCounts(0, 0);
            this.service.refreshDataSource();
        }
    }

    onSort({column, direction}: SortEvent) {
        // resetting other headers
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = '';
            }
        }

        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    onAddUser() {
        if (!this.customerCode) {
            this.toastService.showDanger("No Customer Selected", "Please select a customer first.");
            return;
        }

        const selectedCustomer = this.customers.find(c => c.code === this.customerCode);
        if (!selectedCustomer) {
            this.toastService.showDanger("Invalid Customer", "The selected customer is invalid.");
            return;
        }

        this.addUserForm.reset();
        this.addUserForm.patchValue({
            customerId: selectedCustomer.id
        });

        this.addUserProductGroups = [];
        this.addUserSelectedGroupIds = new Set<string>();
        this.loadingAddUserProductGroups$.next(true);

        this.addUserModalRef = this.modalService.open(this.addUserModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static",
        });

        this.apiService.getProductGroups(selectedCustomer.id).subscribe({
            next: (groups) => {
                this.addUserProductGroups = groups;
                // Auto-select when the customer has only the catch-all group; otherwise force a choice.
                if (groups.length === 1) {
                    this.addUserSelectedGroupIds = new Set([groups[0].id]);
                }
                this.loadingAddUserProductGroups$.next(false);
            },
            error: () => {
                this.loadingAddUserProductGroups$.next(false);
                this.toastService.showDanger("Request Failed", "Failed to load product groups. Please try again.");
            }
        });
    }

    onAddUserDismiss() {
        this.modalConfirmComponent.confirmIfDirty(this.addUserForm, () => this.addUserModalRef.dismiss());
    }

    onAddUserSubmit() {
        if (this.addUserForm.invalid) {
            if (this.addUserForm.controls.email.getError('email')) {
                this.toastService.showDanger("Invalid Email Address", "A valid email address is required.");
                return;
            }

            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        if (this.addUserSelectedGroupIds.size === 0) {
            this.toastService.showDanger("Product Group Required", "Please select at least one product group.");
            return;
        }

        this.savingData = true;
        const user = this.addUserForm.value;

        let body: CreateUserRequest = {
            customerId: user.customerId!,
            fullName: user.fullName!,
            email: user.email!,
            isAdmin: user.isAdmin!,
            productGroupIds: Array.from(this.addUserSelectedGroupIds),
        };

        this.apiService.createUser(body).subscribe({
            next: (response) => {
                this.toastService.showSuccess("Success", "User Successfully Added");
                this.service.data = this.service.data.concat({...response, canResendVerification: false, disabledInCognito: false});
                this.service.refreshDataSource();
            },
            error: (err) => {
                this.savingData = false;
                this.toastService.showDanger("Request Failed", err?.error?.detail || "Please try again.");
            },
            complete: () => {
                this.savingData = false;
                this.addUserModalRef.close();
            }
        });
    }

    onActivity(user: User) {
        openUserActivityDrawer(this.offcanvasService, user);
    }

    onEdit(element: User) {
        this.editForm.reset();
        this.editForm.setValue({
            id: element.id,
            customerId: element.customerId,
            fullName: element.fullName,
            isEnabled: element.isEnabled,
            isAdmin: element.isAdmin,
        });

        this.onEditModalRef = this.modalService.open(this.editModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static",
        });
    }

    onEditDismiss() {
        this.modalConfirmComponent.confirmIfDirty(this.editForm, () => this.onEditModalRef.dismiss());
    }

    onSubmit() {
        if (this.editForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        this.savingData = true;
        const user = this.editForm.value;

        let body: UpdateUserRequest = {
            customerId: user.customerId!,
            fullName: user.fullName!,
            isEnabled: user.isEnabled!,
            isAdmin: user.isAdmin!,
        };

        this.apiService.updateUser(user.id!, body).subscribe({
            next: () => {
                this.toastService.showSuccess("Success", "User Successfully Updated");
                this.service.data = this.service.data.map(data => {
                    if (data.id != user.id) {
                        return data;
                    }

                    const temp = Object.assign({}, data);
                    Object.assign(temp, body);
                    return temp;
                });

                this.service.refreshDataSource();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", "Please try again.");
                throw e;
            },
            complete: () => {
                this.savingData = false;
                this.onEditModalRef.close();
            }
        });
    }

    confirmDeleteUser(user: User) {
        this.modalConfirmComponent.open("Are you sure you want to delete user?", user.email);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                let body: DeleteUserRequest = {
                    customerId: user.customerId!,
                    userId: user.id
                }
                this.globalService.showWaitOverlay();
                this.apiService.deleteUser(body).subscribe({
                    next: () => {
                        this.toastService.showSuccess("Success", "User Successfully Removed.");
                        this.service.data = this.service.data.filter(data =>
                            data.id != user.id);
                        this.service.refreshDataSource();
                        this.globalService.hideWaitOverlay();
                    },
                    error: (e) => {
                        this.toastService.showDanger("Request failed", "Please try again.");
                        console.error("Error occurred during user delete:", e);
                        this.globalService.hideWaitOverlay();
                    },
                    complete: () => {
                    }
                });
            },
            () => {
            },
        );
    }

    confirmResendVerification(user: User) {
        this.modalConfirmComponent.open('Resend the verification email to this user?', user.email);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.globalService.showWaitOverlay();
                this.apiService.resendUserVerification(user.customerId, user.id).subscribe({
                    next: () => {
                        this.toastService.showSuccess('Success', 'Verification email sent.');
                        this.globalService.hideWaitOverlay();
                    },
                    error: (err) => {
                        this.toastService.showDanger('Request Failed', err?.error?.detail || 'Please try again.');
                        this.globalService.hideWaitOverlay();
                    }
                });
            },
            () => {
            },
        );
    }

    onManageProductGroups(user: User) {
        this.productGroupsUser = user;
        this.availableProductGroups = [];
        this.selectedProductGroupIds = new Set<string>();
        this.loadingProductGroups$.next(true);

        this.productGroupsModalRef = this.modalService.open(this.productGroupsModal, {
            modalDialogClass: "modal-dialog-centered modal modal-lg",
            backdrop: "static",
        });

        forkJoin({
            groups: this.apiService.getProductGroups(user.customerId),
            grants: this.apiService.getUserProductGroups(user.customerId, user.id),
        }).subscribe({
            next: ({groups, grants}) => {
                this.availableProductGroups = groups;
                this.selectedProductGroupIds = new Set(grants.map((g: UserProductGroup) => g.id));
                this.loadingProductGroups$.next(false);
            },
            error: () => {
                this.loadingProductGroups$.next(false);
                this.toastService.showDanger("Request Failed", "Failed to load product groups. Please try again.");
            }
        });
    }

    onToggleAddUserProductGroup(groupId: string, event: Event) {
        if ((event.target as HTMLInputElement).checked) {
            this.addUserSelectedGroupIds.add(groupId);
        } else {
            this.addUserSelectedGroupIds.delete(groupId);
        }
    }

    isAddUserProductGroupSelected(groupId: string): boolean {
        return this.addUserSelectedGroupIds.has(groupId);
    }

    get canSubmitAddUser(): boolean {
        return this.addUserForm.valid && this.addUserSelectedGroupIds.size > 0;
    }

    onToggleProductGroup(groupId: string, event: Event) {
        if ((event.target as HTMLInputElement).checked) {
            this.selectedProductGroupIds.add(groupId);
        } else {
            this.selectedProductGroupIds.delete(groupId);
        }
    }

    isProductGroupSelected(groupId: string): boolean {
        return this.selectedProductGroupIds.has(groupId);
    }

    get canSaveProductGroups(): boolean {
        return this.selectedProductGroupIds.size > 0;
    }

    onSaveProductGroups() {
        if (!this.productGroupsUser) {
            return;
        }
        
        if (this.selectedProductGroupIds.size === 0) {
            this.toastService.showDanger("At Least One Group Required", "A user must be granted at least one product group.");
            return;
        }

        this.savingData = true;
        const user = this.productGroupsUser;
        const body: SetUserProductGroupsRequest = {
            productGroupIds: Array.from(this.selectedProductGroupIds),
        };

        this.apiService.setUserProductGroups(user.customerId, user.id, body).subscribe({
            next: () => {
                this.toastService.showSuccess("Success", "Product groups updated.");
                this.savingData = false;
                this.productGroupsModalRef.close();
            },
            error: (err) => {
                this.savingData = false;
                this.toastService.showDanger("Request Failed", err?.error?.detail || "Failed to update product groups. Please try again.");
            }
        });
    }

    closeProductGroupsModal() {
        this.productGroupsModalRef.dismiss();
    }
}
